import { Alert, Button, DataTable, Card, MetricCard, MetricCardGrid, METRIC_READ_CAPTIONS, Skeleton, SkeletonTable, Badge, GroupedBarChart, DonutChart, type DataTableColumn, type DonutSegment } from '@aaimplatform/ui';
import {
  useComplianceDashboardSummary,
  useRegulationUTrend,
  useRiskDistribution,
  useEddReports,
  useComplianceDashboardAlerts,
} from '../../hooks/useComplianceDashboard';
import type { EDDReport, ComplianceDashboardAlert, RiskScoreBucket } from '../../api/services/compliance-dashboard';
import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import { DashboardSectionLabel } from './dashboard-chrome';

/**
 * ComplianceStatusDashboard - Regulatory compliance and audit trail monitoring
 *
 * All data fetched from backend via Prisma-backed endpoints.
 *
 * GH #2913. Every section renders ONE state per read outcome, and no figure is
 * rendered on a path where the read did not happen. The page previously
 * early-returned on `summaryError` only, gated its skeletons on
 * `summaryLoading`, and rendered `summary?.X ?? 0` in the tiles -- so a first
 * fetch that never ran produced `0%` / `0` on an FI compliance surface with no
 * notice attached (`no-fabricated-values.md`: a `??` default supplying an
 * invented figure). It also collapsed a MEASURED 0 and an absent field onto the
 * same `0%`, because `summary?.regulationUPassRate ? ... : '0%'` reads a real
 * zero as falsy.
 *
 * FOUR outcomes per read, not three. TanStack v5 defines `isLoading` as
 * `isPending && isFetching` (@tanstack/query-core, queryObserver), so a FIRST
 * fetch that is PAUSED -- the browser offline before this page mounts,
 * networkMode default -- is pending with isFetching false: past the skeleton
 * branch, and neither error nor success. That state is the one that reads as
 * three states at once if nothing names it.
 *
 * The shape is the Loan Participations tile landed for GH #2742
 * (`src/pages/Dashboard.tsx`): null means NOT READ, never 0; a caption may
 * describe a book only when a successful read produced one.
 */

// The withheld-read captions are the kit's METRIC_READ_CAPTIONS (GH #2917
// deliverable 5); the local names keep this page's call sites readable.
/** A read that ran and rejected. */
const READ_FAILED_CAPTION = METRIC_READ_CAPTIONS.failed;
/**
 * The read has not run. Distinct from READ_FAILED_CAPTION, which states that a
 * read ran and rejected, and distinct from any caption describing a measured
 * figure: there is no figure here, only an absent measurement.
 */
const NOT_READ_CAPTION = METRIC_READ_CAPTIONS.unread;
/**
 * The read SUCCEEDED and the payload omitted this field. Every property of
 * ComplianceDashboardSummary is optional in the canonical spec
 * (schema-registry/platform-admin.openapi.yaml, ComplianceDashboardSummary), so
 * an absent field is a real outcome and is never a zero.
 *
 * This page spelled the same fact as 'Not reported in this read' while
 * CollateralMonitoring.tsx and RiskDashboard.tsx spelled it 'Not reported';
 * the kit's wording is the one in force.
 */
const NOT_REPORTED_CAPTION = METRIC_READ_CAPTIONS.notReported;
const SUMMARY_READ_FAILED_HEADLINE = 'Compliance summary could not be read';

/**
 * The window the Regulation U trend read asks for. The chart title and the
 * empty sentence both state it, so the page passes it rather than relying on
 * the route's default.
 */
const REG_U_TREND_DAYS = 5;

/**
 * Measured-empty sentences for the two chart sections. Each says what is
 * absent and why, where the prior 'No ... data available' named only the
 * absence.
 */
const REG_U_EMPTY_MESSAGE = `No Regulation U checks were recorded for your institution in the last ${REG_U_TREND_DAYS} days.`;
const RISK_EMPTY_MESSAGE =
  'No compliance violations are on record for your institution, so there is no severity distribution to draw.';

/**
 * The four read outcomes, QueryReadOutcome in
 * schema-registry/admin-portal-ui.openapi.yaml.
 */
type ReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

/**
 * The read outcome a section renders from. Derived from the query result fields
 * the hook already returns -- the hooks are plain `useQuery` and are unchanged.
 *
 * `failed` is an error with NO payload: a first read that rejected. A rejected
 * background refetch over a prior successful read keeps that payload, stays
 * `read`, and is named in the page's refresh-failed notice with the time of the
 * read shown (GH #2917 deliverable 4, operator ruling 2026-09-23). The same
 * four outcomes are derived the same way in CollateralMonitoring.tsx and
 * RiskDashboard.tsx.
 */
function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  data: unknown;
}): ReadOutcome {
  if (query.isError && query.data === undefined) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.data !== undefined) return 'read';
  return 'unread';
}

/** The local clock time of a read, for the refresh-failed notice. */
function readTime(dataUpdatedAt: number): string {
  return new Date(dataUpdatedAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * The body text a chart or table section renders when it has no rows to draw.
 * `emptyMessage` is a MEASURED claim about an empty result set, so it is
 * reachable only from the `read` outcome.
 *
 * GH #2917, comment item 9 adds the fourth case. `rows` is `undefined` when a
 * read SUCCEEDED and its body omitted the list. Each of the four compliance
 * lists is optional in the canonical spec, so that body is contract-valid and
 * says nothing about this institution's posture. The service used to answer
 * `[]` on its behalf and the measured-empty sentence printed. The absence now
 * arrives intact and gets a sentence of its own, in the wording
 * CollateralMonitoring.tsx already uses for the same outcome.
 */
function sectionMessage(
  outcome: ReadOutcome,
  subject: string,
  emptyMessage: string,
  rows: readonly unknown[] | undefined,
): string {
  if (outcome === 'failed') return `${subject} could not be read.`;
  if (outcome !== 'read') return `${subject} not yet read.`;
  if (rows === undefined) return `${subject} was not in the response.`;
  return emptyMessage;
}

/**
 * A list as the body carried it, or undefined when the body carried none.
 *
 * The generated type says each list is an array or absent, and the runtime body
 * is not bound by the type: a JSON `null`, or any other non-array, arrives as
 * well. Only an array is a list the response sent, so everything else is
 * treated as the absence it is.
 */
function listOrAbsent<T>(value: T[] | null | undefined): T[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

/**
 * The donut segments a read actually priced.
 *
 * `RiskScoreBucket.value` is OPTIONAL in the canonical spec
 * (schema-registry/platform-admin.openapi.yaml), so a bucket can arrive with a
 * name and no count. `b.value ?? 0` drew that bucket as a 0.0 percent slice and
 * carried it into the chart's total: two measured claims about a count the
 * response never made. GH #2917 deliverable 3.
 *
 * A bucket with no value is withheld from the chart and named beneath it by
 * {@link unvaluedRowList}. A bucket whose count is a measured zero still has a
 * count, so it stays in, and `typeof === 'number'` is what keeps those two
 * apart where a truthiness test would collapse them.
 */
function valuedSegments(rows: readonly RiskScoreBucket[]): DonutSegment[] {
  const segments: DonutSegment[] = [];
  for (const row of rows) {
    if (typeof row.value === 'number') {
      segments.push({ label: row.name ?? '', value: row.value });
    }
  }
  return segments;
}

/**
 * The rows the response left unvalued, as one operator-readable list, or
 * undefined when it left none. A row carrying a name is named; a row carrying
 * neither a name nor a value is counted, because naming it would mean inventing
 * the name too.
 */
function unvaluedRowList(rows: readonly RiskScoreBucket[]): string | undefined {
  const named: string[] = [];
  let unnamed = 0;
  for (const row of rows) {
    if (typeof row.value === 'number') continue;
    if (typeof row.name === 'string' && row.name.length > 0) named.push(row.name);
    else unnamed += 1;
  }
  if (named.length === 0 && unnamed === 0) return undefined;
  const parts = [...named];
  if (unnamed > 0) parts.push(unnamed === 1 ? '1 unnamed row' : `${unnamed} unnamed rows`);
  return parts.join(', ');
}

/** `--` for an absent figure; a measured 0 formats as `0%`. */
function formatPercent(value: number | null | undefined): string {
  return typeof value === 'number' ? `${value}%` : '--';
}

/** `--` for an absent figure; a measured 0 formats as `0`. */
function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' ? String(value) : '--';
}

export function ComplianceStatusDashboard() {
  const summaryQuery = useComplianceDashboardSummary();
  const regUQuery = useRegulationUTrend(REG_U_TREND_DAYS);
  const riskQuery = useRiskDistribution();
  const eddQuery = useEddReports();
  const alertsQuery = useComplianceDashboardAlerts();

  const summaryOutcome = readOutcome(summaryQuery);
  const regUOutcome = readOutcome(regUQuery);
  const riskOutcome = readOutcome(riskQuery);
  const eddOutcome = readOutcome(eddQuery);
  const alertsOutcome = readOutcome(alertsQuery);

  // null means NOT READ -- never an all-zero stand-in. Only a successful read
  // produces a summary, so no `?? 0` fallback remains to turn a withheld read
  // into a measured zero. A rejected refetch keeps the prior summary under the
  // refresh-failed notice (GH #2917 deliverable 4).
  const summary = summaryQuery.data ?? null;

  // Sections whose background refetch rejected after a successful read.
  const refreshFailures = [
    { label: 'summary', query: summaryQuery },
    { label: 'Regulation U trend', query: regUQuery },
    { label: 'risk distribution', query: riskQuery },
    { label: 'EDD reports', query: eddQuery },
    { label: 'alerts', query: alertsQuery },
  ].filter(({ query }) => query.isError && query.data !== undefined);
  const oldestFailedRead =
    refreshFailures.length > 0
      ? Math.min(...refreshFailures.map(({ query }) => query.dataUpdatedAt))
      : null;
  const summaryFailureDetail =
    summaryQuery.error instanceof Error
      ? summaryQuery.error.message
      : SUMMARY_READ_FAILED_HEADLINE;

  // Rows are the read's OUTPUT, so they exist only where a read succeeded.
  //
  // GH #2917, comment item 9. `undefined` now reaches this page for two
  // separate reasons, and `sectionMessage` tells them apart: the read did not
  // produce rows, or the read succeeded and the body omitted the list. The
  // `?? []` that stood on each of these four answered for the server in the
  // second case, and a measured-empty sentence then went out over a list nobody
  // received.
  //
  // Each binding passes through `listOrAbsent`, so a JSON `null` in place of a
  // list, or any other non-array, is also a list nobody received and never
  // reaches the `.length` and `.map` calls below as though it were one.
  const regUTrend =
    regUOutcome === 'read' ? listOrAbsent(regUQuery.data?.trend) : undefined;
  const riskBuckets =
    riskOutcome === 'read' ? listOrAbsent(riskQuery.data?.buckets) : undefined;
  const eddReports =
    eddOutcome === 'read' ? listOrAbsent(eddQuery.data?.reports) : undefined;
  const complianceAlerts =
    alertsOutcome === 'read' ? listOrAbsent(alertsQuery.data?.alerts) : undefined;

  // The donut draws only the buckets the read priced, and names the rest.
  const riskSegments = riskBuckets ? valuedSegments(riskBuckets) : [];
  const riskUnvalued = riskBuckets ? unvaluedRowList(riskBuckets) : undefined;

  /**
   * Why the figure beside it was withheld, or undefined once one printed. It
   * answers the read outcome BEFORE any wording that describes a figure, so a
   * withheld value can never carry a caption asserting something about this
   * institution's posture.
   *
   * GH #2917 deliverable 5. One helper used to return both this and the
   * measured annotation, and both rode MetricCard's `change` slot, which sets
   * its text in monospace and colors it by `changeType`. A withheld read was
   * therefore drawn with the same affordance as a measured movement. The two
   * are now separate: this one rides the `caption` slot, and `measuredNote`
   * below keeps the annotation on `change` with the color it earned.
   */
  const withheldFor = (value: number | null | undefined): string | undefined => {
    if (summaryOutcome === 'failed') return READ_FAILED_CAPTION;
    if (!summary) return NOT_READ_CAPTION;
    if (typeof value !== 'number') return NOT_REPORTED_CAPTION;
    return undefined;
  };

  /** The annotation a figure earned, reachable only once that figure printed. */
  const measuredNote = (
    value: number | null | undefined,
    note?: string,
  ): string | undefined => (withheldFor(value) === undefined ? note : undefined);

  const regulationUPassRate = summary ? summary.regulationUPassRate : null;
  const pendingEddReports = summary ? summary.pendingEddReports : null;
  const overdueEddReports = summary ? summary.overdueEddReports : null;
  const auditTrailCompleteness = summary ? summary.auditTrailCompleteness : null;
  const complianceExceptions30d = summary ? summary.complianceExceptions30d : null;
  const complianceExceptionsChange = summary
    ? summary.complianceExceptionsChange
    : null;

  // The direction arrow is itself a claim about a measured delta -- the prior
  // `(summary?.complianceExceptionsChange ?? 0) < 0` read an absent field as a
  // real zero. It stays neutral unless a read produced a number.
  const exceptionsChangeType: 'positive' | 'negative' | 'neutral' =
    typeof complianceExceptionsChange !== 'number'
      ? 'neutral'
      : complianceExceptionsChange < 0
        ? 'positive'
        : complianceExceptionsChange > 0
          ? 'negative'
          : 'neutral';

  const eddColumns: DataTableColumn<EDDReport>[] = [
    { header: 'Report ID', accessor: 'reportId', align: 'left' },
    { header: 'Loan ID', accessor: 'loanId', align: 'left' },
    {
      header: 'Risk Score',
      accessor: 'riskScore',
      align: 'right',
      monospace: true,
      cell: (value, row) => (
        <span className={
          (row.riskScore ?? 0) >= 70 ? 'text-accent font-semibold' :
          (row.riskScore ?? 0) >= 40 ? 'text-warning font-semibold' :
          ''
        }>
          {String(value)}
        </span>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      align: 'left',
      cell: (value) => (
        <Badge variant={
          value === 'APPROVED' ? 'success' :
          value === 'REJECTED' ? 'error' :
          'warning'
        }>
          {String(value)}
        </Badge>
      ),
    },
    { header: 'Reviewer', accessor: 'reviewer', align: 'left' },
  ];

  const alertColumns: DataTableColumn<ComplianceDashboardAlert>[] = [
    { header: 'Time', accessor: (row) => row.time ? new Date(row.time).toLocaleString() : '', align: 'left' },
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      cell: (value) => (
        <Badge variant={
          value === 'HIGH' || value === 'CRITICAL' ? 'error' :
          value === 'MEDIUM' ? 'warning' :
          'info'
        }>
          {String(value)}
        </Badge>
      ),
    },
    { header: 'Message', accessor: 'message', align: 'left' },
    { header: 'Loan ID', accessor: 'loanId', align: 'left' },
  ];

  return (
    <div>
      {/* Page Heading */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Regulatory status, audit trail, and EDD reports</p>
      </div>

      {/*
        A failed summary read used to blank the whole page via an early return,
        which also hid four INDEPENDENT reads (trend, distribution, EDD reports,
        alerts) that may each have succeeded. The failure is now stated where it
        happened and the rest of the page renders. Four tiles reading `--` with
        nothing said would be a quieter form of the same defect: the operator
        sees withheld figures and no reason to distinguish them from a clean
        compliance posture.
      */}
      {oldestFailedRead !== null && (
        <div className="mb-6" role="status">
          <Alert type="warning" title="Compliance status refresh failed">
            <div className="space-y-2">
              <div className="text-xs">
                {`The latest refresh of ${refreshFailures.map(({ label }) => label).join(', ')} failed. The figures shown are from the read at ${readTime(oldestFailedRead)}.`}
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  refreshFailures.forEach(({ query }) => {
                    void query.refetch();
                  });
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {summaryOutcome === 'failed' && (
        <div className="mb-6" role="alert">
          <Alert type="error" title={SUMMARY_READ_FAILED_HEADLINE}>
            <div className="space-y-2">
              <div className="text-xs">{summaryFailureDetail}</div>
              <div className="text-xs">
                The four figures below are withheld, not zero. Nothing here
                states your institution&apos;s compliance posture -- the read
                itself failed.
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void summaryQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Key Metrics */}
      <MetricCardGrid className="mb-8">
        {summaryOutcome === 'loading' ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <MetricCard
              label="Regulation U Pass Rate"
              value={formatPercent(regulationUPassRate)}
              caption={withheldFor(regulationUPassRate)}
            />
            <MetricCard
              label="Pending EDD Reports"
              value={formatCount(pendingEddReports)}
              caption={withheldFor(pendingEddReports)}
              change={measuredNote(
                pendingEddReports,
                typeof overdueEddReports === 'number' && overdueEddReports > 0
                  ? `${overdueEddReports} overdue (>72h)`
                  : undefined,
              )}
              changeType="neutral"
            />
            <MetricCard
              label="Audit Trail Completeness"
              value={formatPercent(auditTrailCompleteness)}
              caption={withheldFor(auditTrailCompleteness)}
            />
            <MetricCard
              label="Compliance Exceptions (30d)"
              value={formatCount(complianceExceptions30d)}
              caption={withheldFor(complianceExceptions30d)}
              change={measuredNote(
                complianceExceptions30d,
                // A zero delta printed '0 vs last month', which reads as zero
                // exceptions last month rather than no change (UI run r3,
                // fi-g4-13).
                typeof complianceExceptionsChange === 'number'
                  ? complianceExceptionsChange === 0
                    ? 'No change from last month'
                    : `${complianceExceptionsChange > 0 ? '+' : ''}${complianceExceptionsChange} vs last month`
                  : undefined,
              )}
              changeType={exceptionsChangeType}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-8">
        <div className="lg:col-span-8">
          {regUOutcome === 'loading' ? (
            <Skeleton className="h-[300px]" />
          ) : regUTrend && regUTrend.length > 0 ? (
            <GroupedBarChart
              title={`Regulation U Compliance Checks (Last ${REG_U_TREND_DAYS} Days)`}
              data={regUTrend}
              dataKeys={[
                { key: 'passed', name: 'Passed', color: 'rgb(var(--color-growth))' },
                { key: 'failed', name: 'Failed', color: 'rgb(var(--color-accent))' },
              ]}
              xAxisKey="date"
              height={300}
            />
          ) : (
            // h-full on both stand-in cards: the grid item stretches, the Card
            // follows its text unless told to fill it, and the two cards of
            // this row ended about 20px apart (UI run r3, fi-g4-14).
            <Card className="h-full">
              <div className="py-12 text-center text-tertiary">
                {sectionMessage(
                  regUOutcome,
                  'Regulation U compliance data',
                  REG_U_EMPTY_MESSAGE,
                  regUTrend,
                )}
              </div>
            </Card>
          )}
        </div>
        <div className="lg:col-span-4">
          {riskOutcome === 'loading' ? (
            <Skeleton className="h-[300px]" />
          ) : riskSegments.length > 0 ? (
            <div>
              <DonutChart
                title="Risk Score Distribution"
                data={riskSegments}
                size={220}
                thickness={40}
              />
              {/* The chart's own total is a sum over what it drew, so the rows
                  it could not draw are named right beside it. A caption here is
                  the page's own element: DonutChart exposes no caption slot,
                  the way MetricCard now does. */}
              {riskUnvalued && (
                <p className="mt-2 text-xs font-sans text-tertiary">
                  {`Withheld from the chart and the total, no value in the response: ${riskUnvalued}.`}
                </p>
              )}
            </div>
          ) : (
            <Card className="h-full">
              <div className="py-12 text-center text-tertiary">
                {riskUnvalued
                  ? `Risk score data was returned without values: ${riskUnvalued}.`
                  : sectionMessage(
                      riskOutcome,
                      'Risk score data',
                      RISK_EMPTY_MESSAGE,
                      riskBuckets,
                    )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* EDD Report Queue */}
      <DashboardSectionLabel className="mt-8">Enhanced Due Diligence (EDD) Reports</DashboardSectionLabel>
      {eddOutcome === 'loading' ? (
        <SkeletonTable rows={4} columns={5} />
      ) : eddReports && eddReports.length > 0 ? (
        <Card className="mb-8">
          <DataTable
            columns={eddColumns}
            data={eddReports}
            striped
            hoverable
          />
        </Card>
      ) : (
        <Card className="mb-8">
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(eddOutcome, 'EDD reports', 'No EDD reports pending', eddReports)}
          </div>
        </Card>
      )}

      {/* Recent Compliance Alerts */}
      <DashboardSectionLabel>Recent Compliance Alerts (Last 24h)</DashboardSectionLabel>
      {alertsOutcome === 'loading' ? (
        <SkeletonTable rows={3} columns={4} />
      ) : complianceAlerts && complianceAlerts.length > 0 ? (
        <Card>
          <DataTable
            columns={alertColumns}
            data={complianceAlerts}
            striped
            hoverable
          />
        </Card>
      ) : (
        <Card>
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(
              alertsOutcome,
              'Compliance alerts',
              'No compliance alerts in the last 24 hours',
              complianceAlerts,
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
